import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { da } from 'date-fns/locale';
import theme from './theme';
import { useAuthStore } from './store/authStore';
import { Layout } from './components/layout/Layout';
import { LoginPage } from './pages/Login';
import { ApplyOrganizationPage } from './pages/ApplyOrganization';
import { SetPassword } from './pages/SetPassword';
import { DashboardPage } from './pages/Dashboard';
import { CustomerListPage } from './pages/customers/CustomerList';
import { CustomerDetail } from './pages/customers/CustomerDetail';
import { CustomerCreate } from './pages/customers/CustomerCreate';
import { Tasks } from './pages/Tasks';
import { Reports } from './pages/Reports';
import { Settings } from './pages/settings/Settings';
import { UserManagement } from './pages/settings/UserManagement';
import { TmsCustomerMappings } from './pages/settings/TmsCustomerMappings';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { OrganizationsList } from './pages/admin/OrganizationsList';
import { FlowtaleDocs } from './pages/admin/FlowtaleDocs';
import { TmsLog } from './pages/admin/TmsLog';
import { UnmappedBookings } from './pages/UnmappedBookings';
import { CustomerMerge } from './pages/CustomerMerge';
import { Box, CircularProgress } from '@mui/material';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user?.isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  const { refreshUser, accessToken } = useAuthStore();

  useEffect(() => {
    if (accessToken) {
      refreshUser();
    } else {
      useAuthStore.setState({ isLoading: false });
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={da}>
          <CssBaseline />
          <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/apply" element={<ApplyOrganizationPage />} />
            <Route path="/set-password" element={<SetPassword />} />

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="customers" element={<CustomerListPage />} />
              <Route path="customers/new" element={<CustomerCreate />} />
              <Route path="customers/:id" element={<CustomerDetail />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<Settings />} />
              <Route path="settings/users" element={<UserManagement />} />
              <Route path="settings/tms-mappings" element={<TmsCustomerMappings />} />
              <Route path="unmapped-bookings" element={<UnmappedBookings />} />
              <Route path="customer-merge" element={<CustomerMerge />} />

              <Route
                path="admin"
                element={
                  <SuperAdminRoute>
                    <AdminDashboard />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="admin/organizations"
                element={
                  <SuperAdminRoute>
                    <OrganizationsList />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="admin/flowtale-docs"
                element={
                  <SuperAdminRoute>
                    <FlowtaleDocs />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="admin/flowtale-log"
                element={
                  <SuperAdminRoute>
                    <TmsLog />
                  </SuperAdminRoute>
                }
              />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrowserRouter>
        </LocalizationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
